/**
 * Effect classification for the Dossier Effects tab.
 *
 * Perspective: ATTACKER. "Is this effect good or bad for me as someone trying to attack
 * or thieve this province?" — not what it means to the province owner.
 *
 * This map is reconciled against EffectType.java (33 values, incl. SF-186 TREE_OF_GOLD).
 * effectClassification.test.ts asserts:
 *  - Every EffectType enum value has an explicit mapping
 *  - No mapping references a key not in the enum
 *  - Unknown value falls back to BUFF + console.warn
 */

export type EffectCategory = 'DEFENSE' | 'DEBUFF' | 'BUFF';

/** All 33 EffectType enum values — must match EffectType.java exactly. */
export const EFFECT_TYPES = [
  'PLAGUE',
  'FERTILE_LANDS',
  'DROUGHT',
  'VERMIN',
  'INSPIRE_ARMY',
  'FANATISM',
  'MINOR_PROTECTION',
  'GREATER_PROTECTION',
  'QUICK_FEET',
  'ANONYMITY',
  'AGGRESSION',
  'TOWNWATCH',
  'RIOT',
  'MAGES_FURY',
  'MAGIC_SHIELD',
  'CHASTITY',
  'BUILDERS_BOON',
  'MYSTIC_AURA',
  'NATURES_BLESSING',
  'LOVE_AND_PEACE',
  'CLEAR_SIGHT',
  'FOUNTAIN_OF_KNOWLEDGE',
  'PATRIOTISM',
  'STORMS',
  'GREED',
  'BRIBED_THIEVES',
  'BRIBED_GENERALS',
  'SHADOWLIGHT',
  'PARADISE',
  'TREE_OF_GOLD',
  'PITFALLS',
  'EXPLOSIONS',
  'WAR_SPOILS',
] as const;

export type EffectType = (typeof EFFECT_TYPES)[number];

/**
 * Static classification map from EffectType → EffectCategory.
 *
 * DEFENSE — hardens the target (makes them harder to hit / retaliate faster):
 *   MINOR_PROTECTION, GREATER_PROTECTION — DME boost (verified in MilitarySystem)
 *   MAGIC_SHIELD — DME boost (SF-16D)
 *   MYSTIC_AURA — single-use spell shield; blocks enemy spells
 *   ANONYMITY — ATTACK_TIME_MOD = 1.15 (verified in MilitarySystem.computeAttackTime)
 *   QUICK_FEET — owner's attacks are faster → faster retaliation on you
 *   TOWNWATCH — peasants/4 added to raw defense (verified in MilitarySystem)
 *   CLEAR_SIGHT — flat thief-catch chance boost (SF-59)
 *   NATURES_BLESSING — blocks Storms/Drought, cures Plague (prevents your debuffs landing)
 *   BUILDERS_BOON — construction speedup; damaged buildings rebuild faster
 *   SHADOWLIGHT — indefinite cloak; reveals attacker on next thievery (SF-91e).
 *                 Reveals are post-hoc, so SHADOWLIGHT does not block the op —
 *                 but knowing the defender will see your name is a deterrent
 *                 from the attacker POV.
 *
 * DEBUFF — weakens the target (opportunity window):
 *   PLAGUE — -15% DME (verified in computeDefensiveMilitaryEfficiency)
 *   DROUGHT — food production penalty
 *   VERMIN — food stockpile decay
 *   RIOT — income/draft penalty
 *   MAGES_FURY — per-tick damage to target
 *   CHASTITY — birth-rate suppression
 *   STORMS — per-tick population damage
 *   GREED — wage & draft cost penalty
 *   BRIBED_THIEVES — -10% TPA for the target
 *   BRIBED_GENERALS — 20% chance per combat for +15% loss multiplier on victim
 *   AGGRESSION — DEBUFF (locked Phase 3 D20): zeroes soldier-based defense
 *   PITFALLS — +25% defensive losses on target; opportunity window for attacker (SF-91b)
 *                (UnitSystem.java:65); from TM attacker's POV zeroed soldier defense is opportunity
 *   EXPLOSIONS — 50% chance per aid shipment to/from target reduced 55-80% (SF-91c);
 *                disrupts target's logistics — DEBUFF from attacker POV
 *
 * BUFF — target stronger / richer (bigger payoff but harder to hit):
 *   FERTILE_LANDS — food production bonus
 *   INSPIRE_ARMY — army training/size bonus → bigger grab when you succeed
 *   LOVE_AND_PEACE — birth rate bonus → more peasants
 *   FOUNTAIN_OF_KNOWLEDGE — science bonus multiplier
 *   PATRIOTISM — draft speed bonus
 *   FANATISM — +5% OME, -3% DME. Net signal "bigger retaliator" (attacker POV)
 *   WAR_SPOILS — SF-96: doesn't change combat outcome or defense at all; only affects the
 *                target's OWN outgoing TM attacks (instant land credit, Ambush-proof). Signals
 *                a more aggressive/consolidated kingdom, same reasoning as PATRIOTISM/
 *                FOUNTAIN_OF_KNOWLEDGE ("target stronger overall", not defense-specific)
 */
const CLASSIFICATION_MAP: Record<EffectType, EffectCategory> = {
  // DEFENSE
  MINOR_PROTECTION:    'DEFENSE',
  GREATER_PROTECTION:  'DEFENSE',
  MAGIC_SHIELD:        'DEFENSE',
  MYSTIC_AURA:         'DEFENSE',
  ANONYMITY:           'DEFENSE',
  QUICK_FEET:          'DEFENSE',
  TOWNWATCH:           'DEFENSE',
  CLEAR_SIGHT:         'DEFENSE',
  NATURES_BLESSING:    'DEFENSE',
  BUILDERS_BOON:       'DEFENSE',
  SHADOWLIGHT:         'DEFENSE',
  // DEBUFF
  PLAGUE:              'DEBUFF',
  DROUGHT:             'DEBUFF',
  VERMIN:              'DEBUFF',
  RIOT:                'DEBUFF',
  MAGES_FURY:          'DEBUFF',
  CHASTITY:            'DEBUFF',
  STORMS:              'DEBUFF',
  GREED:               'DEBUFF',
  BRIBED_THIEVES:      'DEBUFF',
  BRIBED_GENERALS:     'DEBUFF',
  AGGRESSION:          'DEBUFF',  // D20: locked as DEBUFF (UnitSystem.java:65)
  PITFALLS:            'DEBUFF',  // SF-91b: target suffers +25% defensive losses — opportunity window for attacker
  EXPLOSIONS:          'DEBUFF',  // SF-91c: 50% chance to reduce aid shipments 55-80% — disrupts target's logistics
  // BUFF
  FERTILE_LANDS:       'BUFF',
  INSPIRE_ARMY:        'BUFF',
  LOVE_AND_PEACE:      'BUFF',
  FOUNTAIN_OF_KNOWLEDGE: 'BUFF',
  PATRIOTISM:          'BUFF',
  FANATISM:            'BUFF',
  PARADISE:            'BUFF',  // SF-91a: instant land grant — never persists as effect, placeholder only
  TREE_OF_GOLD:        'BUFF',  // SF-186: instant gold grant — never persists as effect, placeholder only
  WAR_SPOILS:          'BUFF',  // SF-96: instant TM land credit + Ambush-proofs the caster's TM attacks
};

/**
 * Returns the attacker-perspective classification for an effect type.
 *
 * Unknown effect types default to BUFF with a console.warn (runtime safety).
 * In practice this branch should never be hit: effectClassification.test.ts
 * asserts every known EffectType has an explicit mapping.
 */
export function classifyEffect(effectType: string): EffectCategory {
  const known = CLASSIFICATION_MAP[effectType as EffectType];
  if (known !== undefined) return known;
  console.warn(`[effectClassification] Unknown effect type: ${effectType} — defaulting to BUFF`);
  return 'BUFF';
}
